/**
 * Build-time SEO for VitePress static-site generation.
 *
 * VitePress 1.6 does **not** use `unhead`, so the runtime `useSEO`/`applyHead`
 * composable is a no-op against the SSG HTML. The SSG-correct sink is the
 * `transformPageData(pageData)` build hook: writing `<meta>`/`<link>`/JSON-LD
 * into `pageData.frontmatter.head` (VitePress bakes those into the rendered
 * `<head>`) and overwriting `pageData.title` / `pageData.description` (VitePress
 * renders the `<title>` — via `titleTemplate` — and the `description` meta from
 * those two fields).
 *
 * This factory generalises the bespoke `buildSeoHead`/`transformPageData` that
 * shipped inline in a site's `.vitepress/config.ts`. It reuses the shared,
 * framework-agnostic resolver (`resolvePageSeo`, `generateOpenGraphMeta`,
 * `generateTwitterMeta`, `generateJsonLd`) for global + page meta / OG / Twitter
 * / canonical and the global JSON-LD knowledge graph, and delegates **page-type
 * JSON-LD** (e.g. Article / Place / CollectionPage / Service / FAQPage +
 * BreadcrumbList) to a *pluggable* rule set the site supplies. None of the
 * real-estate (or any other vertical's) schema logic lives in this package — it
 * is all site CONFIG.
 *
 * It is the VitePress counterpart to the Vue-SPA per-route emitter in
 * `dcsSeoPlugin({ emitStaticHtml: true })`; both produce identical global
 * meta/OG/Twitter/JSON-LD from the same `seo.yaml` via the shared resolver.
 *
 * @example
 * ```ts
 * // docs/.vitepress/config.ts
 * import { createSeoTransformPageData } from '@duffcloudservices/cms/plugins'
 * import seoConfig from '../../.dcs/seo.yaml'
 *
 * export default defineConfig({
 *   transformPageData: createSeoTransformPageData({
 *     seoConfig,
 *     pageTypeRules: [
 *       { match: (ctx) => ctx.route.startsWith('/blogs/'), build: (ctx) => [ ... ] },
 *       // ...Place / CollectionPage / Service / FAQPage rules
 *     ],
 *   }),
 * })
 * ```
 */

import type {
  SeoConfiguration,
  GlobalSeoConfig,
  SeoOpenGraphConfig,
} from '../types/seo'
import {
  resolvePageSeo,
  generateOpenGraphMeta,
  generateTwitterMeta,
  generateJsonLd,
} from './headTags'
import {
  buildGlobalGraph,
  buildBreadcrumbList,
  buildBlogPosting,
  buildFaqPage,
  breadcrumbTrailFromRoute,
  graphAbsorbs,
  slugToTitle,
  type ReviewSource,
  type FaqSource,
  type BlogMeta,
} from './schemaGraph'
import { escapeJsonLd } from './spliceHeadHtml'
import type { SeoSchemaConfig } from '../types/seo'

/**
 * A VitePress `head` entry. Mirrors VitePress's `HeadConfig` without taking a
 * dependency on the `vitepress` package (which is not a dependency of this
 * library). The tuple forms are:
 *   ['meta', { name|property, content }]
 *   ['link', { rel, href, ... }]
 *   ['script', { type: 'application/ld+json' }, '<serialised json>']
 */
export type VitePressHeadConfig =
  | [string, Record<string, string>]
  | [string, Record<string, string>, string]

/**
 * The minimal slice of VitePress's `PageData` this factory reads and mutates.
 * Typed structurally so callers can pass VitePress's real `PageData` without a
 * cast and without this package importing `vitepress`.
 */
export interface VitePressPageData {
  /** Source-relative path, e.g. `index.md`, `blogs/my-post.md`. */
  relativePath: string
  /** Dynamic-route params (e.g. `{ topic: 'home-buying' }`). */
  params?: Record<string, unknown>
  /**
   * Page frontmatter; `head` is appended to here.
   *
   * Deliberately `any`: site `seo.config.ts` rules dot-walk arbitrary frontmatter,
   * so `unknown` would be a breaking API change for every consuming site.
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  frontmatter: Record<string, any>
  /** VitePress page title (drives `<title>` via `titleTemplate`). */
  title?: string
  /** VitePress page description (drives the `description` meta). */
  description?: string
  [key: string]: unknown
}

/**
 * Context handed to the site's page-type rules and resolver hooks. Everything a
 * site needs to derive its title/description/og/schemas for one page, computed
 * once per page by the factory.
 */
export interface SeoPageContext {
  /** Route path, e.g. `/`, `/blogs/my-post`, `/locations/birmingham`. */
  route: string
  /** Slug: `'home'` for `/`, otherwise the route without its leading slash. */
  slug: string
  /** Absolute canonical URL for this route. */
  canonical: string
  /** Normalised site base URL (no trailing slash), e.g. `https://example.com`. */
  siteUrl: string
  /**
   * The page's frontmatter (read-only convenience; same object as pageData).
   *
   * Deliberately `any` — mirrors `VitePressPageData.frontmatter` above.
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  frontmatter: Record<string, any>
  /** The resolved global SEO config block. */
  global: GlobalSeoConfig
  /** The full VitePress page data (for rules that need more than the above). */
  pageData: VitePressPageData
}

/**
 * The resolved per-page title / description / OG type a site may override.
 * Returned by the optional `resolvePage` hook so a site can apply its own
 * per-page-type title precedence (e.g. "{City} Luxury Real Estate") and decide
 * whether that title should win over VitePress's `titleTemplate`.
 */
export interface ResolvedPageOverrides {
  /**
   * The page title. When `setPageTitle` is true this is written to
   * `pageData.title` (VitePress then applies `titleTemplate`).
   */
  title?: string
  /**
   * When true, `title` is written back to `pageData.title`. Leave false for
   * pages whose frontmatter title should remain authoritative (e.g. blog posts
   * that already carry a good `<h1>`/title).
   */
  setPageTitle?: boolean
  /** The meta description. Written to `pageData.description` when truthy. */
  description?: string
  /** Open Graph type override (e.g. `'article'`, `'profile'`). */
  ogType?: SeoOpenGraphConfig['type']
  /** Open Graph image URL override (e.g. a post's header image). */
  ogImage?: string
  /**
   * Extra Open Graph fields to merge into the OG config (e.g. `publishedTime`,
   * `modifiedTime`, `section`, `tags`). The shared OG generator emits the
   * matching `article:*` tags when `ogType === 'article'`. This is how a site
   * supplies article metadata (date/category) without that logic living in the
   * package. Lower precedence than `ogType`/`ogImage`/`ogTitle`/`ogDescription`.
   */
  og?: Partial<SeoOpenGraphConfig>
  /** Keywords override (comma-separated) for the `keywords` meta. */
  keywords?: string
  /**
   * Open Graph title override. Defaults to `title` so og:title tracks <title>.
   */
  ogTitle?: string
  /**
   * Open Graph description override. Defaults to `description`.
   */
  ogDescription?: string
}

/** A single pluggable page-type rule: when `match` is true, emit `build`. */
export interface SeoPageTypeRule {
  /** Return true when this rule applies to the page (by route/slug/etc.). */
  match: (ctx: SeoPageContext) => boolean
  /** Build the page-type JSON-LD objects to emit (already plain objects). */
  build: (ctx: SeoPageContext) => Array<Record<string, unknown>>
}

export interface CreateSeoTransformPageDataOptions {
  /** The parsed `.dcs/seo.yaml` (global graph + per-page meta). */
  seoConfig: SeoConfiguration | undefined
  /**
   * Pluggable page-type rules. Evaluated in order; **every** matching rule's
   * `build` output is emitted (so a route can contribute both a primary schema
   * and a BreadcrumbList from one rule, or be matched by several). The
   * real-estate BlogPosting / Place / CollectionPage / Service / FAQPage logic
   * is supplied here by the site — never hardcoded in this package.
   */
  pageTypeRules?: SeoPageTypeRule[]
  /**
   * Optional hook to override per-page title / description / OG before tags are
   * built — the site's title precedence and per-type description fallbacks.
   * Receives the same context as the rules. Anything it omits falls back to the
   * resolver / frontmatter defaults.
   */
  resolvePage?: (ctx: SeoPageContext) => ResolvedPageOverrides | undefined
  /**
   * Map a `relativePath` (+ params) to a route. Defaults to a VitePress-correct
   * implementation: `index` becomes `/`, a trailing `/index` is dropped, `.md`
   * is stripped, and dynamic `[name]` segments are substituted from
   * `pageData.params`. Override only for unusual routing.
   */
  relativePathToRoute?: (relativePath: string, params?: Record<string, unknown>) => string
  /**
   * Emit a `<meta name="keywords">` from the resolved/overridden keywords.
   * Default true (parity with the bespoke KDH emitter, which emitted keywords).
   */
  includeKeywords?: boolean

  // ── Schema-completeness (shared with the SPA path via ./schemaGraph.ts) ────

  /**
   * Emit the cross-linked global `@graph` spine (Organization + WebSite + the
   * promoted LocalBusiness node) in place of the flat per-schema global JSON-LD.
   * When ON, the LocalBusiness subtype in `global.schemas` is ABSORBED into the
   * graph (not emitted twice); other global schemas (Person, etc.) are still
   * emitted standalone. Default `false`, so existing sites (which supply their
   * own graph via `global.schemas`) are unchanged until they opt in.
   */
  emitGraph?: boolean
  /**
   * Emit an automatic `BreadcrumbList` derived from the route depth on every
   * non-home page. Default `false` (sites that already emit breadcrumbs via
   * `pageTypeRules` should leave this off to avoid duplicates). Intermediate /
   * leaf crumb titles come from `breadcrumbTitles(ctx)` when provided, else a
   * slug-derived Title Case.
   */
  emitBreadcrumbs?: boolean
  /**
   * Map a context to a `{ route → title }` map for breadcrumb hop labels (e.g.
   * `{ '/': 'Home', '/blogs': 'Blog' }`). Missing entries fall back to a
   * slug-derived title. Only consulted when `emitBreadcrumbs` is true.
   */
  breadcrumbTitles?: (ctx: SeoPageContext) => Record<string, string> | undefined
  /**
   * Emit an automatic `BlogPosting` (author/publisher as `@id` refs) for blog
   * routes, derived from frontmatter (`title`/`date`/`image`/`description`).
   * `blogMatch` decides which routes are posts; default off. Skipped when a
   * `pageTypeRule` already emitted a `BlogPosting` for the page.
   */
  emitBlogPosting?: boolean
  /** Predicate selecting blog-post routes for `emitBlogPosting`. */
  blogMatch?: (ctx: SeoPageContext) => boolean
  /**
   * Emit an automatic, honesty-gated `FAQPage` from `frontmatter.faq` (an array
   * of `{ q, a }` / `{ question, answer }`). Emits nothing when the frontmatter
   * carries no structured Q&A. Default `false`.
   */
  emitFaq?: boolean
  /**
   * Provide REAL review items for the LocalBusiness node in the `@graph` (only
   * used when `emitGraph` is true). Honesty-gated downstream — items lacking a
   * rating/text/authorName are dropped, and an empty result emits no Review or
   * aggregateRating. Default: none.
   */
  resolveReviews?: (ctx: SeoPageContext) => ReviewSource[] | undefined
  /**
   * Provide the free-text trade/occupational license (the `business.license`
   * content.yaml key) for the LocalBusiness node in the `@graph` (only used when
   * `emitGraph` is true). When non-empty a `hasCredential`
   * (`EducationalOccupationalCredential`, `credentialCategory: "license"`) is
   * added to the business node; empty/absent ⇒ nothing. Default: none.
   */
  resolveLicense?: (ctx: SeoPageContext) => string | undefined

  /** Enable debug logging of the emitted head per page. */
  debug?: boolean
}

/** Default VitePress route derivation (matches the bespoke KDH helper). */
export function defaultRelativePathToRoute(
  relativePath: string,
  params?: Record<string, unknown>
): string {
  let stem = (relativePath ?? '').replace(/\.md$/i, '')
  // Substitute dynamic [param] segments (e.g. topics/[topic] → topics/home-buying).
  if (params) {
    for (const [key, value] of Object.entries(params)) {
      if (value == null) continue
      stem = stem.replace(`[${key}]`, String(value))
    }
  }
  if (stem === 'index') return '/'
  if (stem.endsWith('/index')) stem = stem.slice(0, -'/index'.length)
  return `/${stem}`
}

function normaliseSiteUrl(siteUrl: string | undefined): string {
  return (siteUrl ?? '').replace(/\/$/, '')
}

/**
 * A `<script type="application/ld+json">` VitePress head tuple.
 *
 * VitePress's `renderHead` writes this third tuple element into the script's
 * innerHTML WITHOUT escaping it for the HTML script context. Untrusted CMS free
 * text (review bodies, FAQ Q&A, blog descriptions) carrying `</script>` would
 * therefore break out and inject markup (stored XSS). We escape the serialized
 * JSON-LD via the SAME shared guard the SPA emit path uses (`escapeJsonLd`),
 * which `\u`-escapes `<`/`>`/`&` while keeping the payload valid JSON.
 */
function ldScript(obj: Record<string, unknown>): VitePressHeadConfig {
  return ['script', { type: 'application/ld+json' }, escapeJsonLd(JSON.stringify(obj))]
}

/**
 * Build just the SEO head tuples for a page (no `pageData` mutation). Exposed
 * separately so it is unit-testable without a VitePress `pageData` round-trip
 * and reusable by callers that manage the `head`/`title` sinks themselves.
 *
 * @returns `{ head, title, description }` — the head tuples to append, and the
 *   final title/description (already overridden) the caller should write to
 *   `pageData` when `applyTitle`/`applyDescription` are appropriate.
 */
export function buildVitePressSeoHead(
  pageData: VitePressPageData,
  options: CreateSeoTransformPageDataOptions
): { head: VitePressHeadConfig[]; title?: string; description?: string; setPageTitle: boolean } {
  const {
    seoConfig,
    pageTypeRules = [],
    resolvePage,
    relativePathToRoute = defaultRelativePathToRoute,
    includeKeywords = true,
    emitGraph = false,
    emitBreadcrumbs = false,
    breadcrumbTitles,
    emitBlogPosting = false,
    blogMatch,
    emitFaq = false,
    resolveReviews,
    resolveLicense,
  } = options

  const global = seoConfig?.global ?? {}
  const siteUrl = normaliseSiteUrl(global.siteUrl)

  const fm = pageData.frontmatter ?? {}
  const route = relativePathToRoute(pageData.relativePath, pageData.params)
  const slug = route === '/' ? 'home' : route.slice(1)
  const canonical = route === '/' ? `${siteUrl}/` : `${siteUrl}${route}`

  const ctx: SeoPageContext = {
    route,
    slug,
    canonical,
    siteUrl,
    frontmatter: fm,
    global,
    pageData,
  }

  // Resolve global + per-page SEO from the shared resolver. We pass the page's
  // frontmatter title as the fallback so un-configured pages still get a
  // sensible (templated) title; seo.yaml `pages.<slug>.title` still wins.
  const resolved = resolvePageSeo(slug, route, seoConfig, fm.title)

  // Site-provided per-page overrides (title precedence + per-type description).
  const overrides = resolvePage?.(ctx) ?? {}

  // ── Title handling ────────────────────────────────────────────────────────
  // Two titles matter for VitePress:
  //   • `rawTitle`  → written to `pageData.title` (VitePress applies its OWN
  //                   `titleTemplate`, so the <title> element is templated once).
  //   • `templated` → used for og:title / twitter:title so they match the final
  //                   <title> element (the shared resolver's og:title fix).
  // A site override (`overrides.title`) is the RAW per-type title; we template
  // it ourselves for OG/Twitter using the same precedence as the resolver.
  const rawTitle =
    overrides.title ?? pageSpecificSeoTitle(slug, seoConfig) ?? fm.title ?? resolved.title
  const templated = applyTitleTemplate(rawTitle, global, resolved)

  // Description precedence mirrors the bespoke emitter: a site per-type override
  // → the explicit seo.yaml `page.description` → the page frontmatter → the
  // global default. (The shared resolver collapses frontmatter and the global
  // default, so we read the explicit page description directly to keep
  // frontmatter ahead of the global fallback.)
  const pageSeoDescription = seoConfig?.pages?.[slug]?.description
  const description =
    overrides.description ||
    pageSeoDescription ||
    fm.description ||
    global.defaultDescription ||
    ''
  const keywords =
    overrides.keywords ||
    seoConfig?.pages?.[slug]?.keywords ||
    (Array.isArray(fm.tags) ? fm.tags.join(', ') : '')

  // Open Graph type/image: site override → seo.yaml → frontmatter → default.
  const ogType =
    overrides.ogType || resolved.openGraph.type
  const ogImage =
    overrides.ogImage || resolved.openGraph.image || fm.headerImage || fm.image || global.images?.ogDefault

  const head: VitePressHeadConfig[] = []

  // Keywords (parity: the bespoke emitter pushed keywords when present).
  if (includeKeywords && keywords) {
    head.push(['meta', { name: 'keywords', content: keywords }])
  }

  // Robots.
  head.push(['meta', { name: 'robots', content: resolved.robots }])

  // Canonical.
  head.push(['link', { rel: 'canonical', href: canonical }])

  // Verification.
  if (global.verification?.google) {
    head.push(['meta', { name: 'google-site-verification', content: global.verification.google }])
  }
  if (global.verification?.bing) {
    head.push(['meta', { name: 'msvalidate.01', content: global.verification.bing }])
  }

  // Open Graph — reuse the shared generator, with our resolved title/desc/og
  // overrides applied via a synthetic OG config so the output matches the SPA
  // emitter byte-for-byte (article:* fields included when type === 'article').
  // og:title tracks the TEMPLATED title (the resolver's og:title fix), so it
  // matches the final <title> element rather than the raw per-type title.
  //
  // We DON'T spread `resolved.openGraph` wholesale: for an un-configured page
  // its `description` is the global default, which would wrongly win over this
  // page's actual `description`. We instead keep only the page's *explicit* OG
  // fields (from seo.yaml) and let `description`/`title` fall through correctly.
  const pageOg = seoConfig?.pages?.[slug]?.openGraph
  const ogConfig: SeoOpenGraphConfig = {
    ...pageOg,
    // Site-supplied extra OG fields (publishedTime/section/tags/…) — lower
    // precedence than the explicit type/title/desc/image resolved below.
    ...overrides.og,
    type: ogType,
    title: overrides.ogTitle || templated,
    description: overrides.ogDescription || pageOg?.description || description,
    ...(ogImage ? { image: ogImage } : {}),
    url: canonical,
  }
  for (const t of generateOpenGraphMeta(ogConfig, global, templated, description, canonical)) {
    head.push(['meta', { property: t.property, content: t.content }])
  }

  // Twitter — shared generator (twitter:title also tracks the templated title).
  for (const t of generateTwitterMeta(resolved.twitter, global, templated, description)) {
    head.push(['meta', { name: t.name, content: t.content }])
  }

  // JSON-LD: the global knowledge graph (RealEstateAgent / Person / WebSite /
  // Organization / …) on every page, via the shared generator.
  //
  // When `emitGraph` is on, the shared spine (Organization + WebSite + the
  // promoted LocalBusiness node) is emitted as a single `@graph` block and the
  // LocalBusiness subtype is removed from the flat per-schema emission so it is
  // not duplicated. Other global schemas (Person, etc.) still emit standalone.
  let globalSchemas: SeoSchemaConfig[] = resolved.schemas
  if (emitGraph) {
    const reviews = resolveReviews?.(ctx)
    const license = resolveLicense?.(ctx)
    const graph = buildGlobalGraph(global, { reviews, license })
    if (graph.length > 0) {
      for (const obj of graph) head.push(ldScript(obj))
      // Drop the schemas the @graph now provides canonically (LocalBusiness +
      // any Organization/WebSite) so they are not emitted twice.
      globalSchemas = resolved.schemas.filter((s) => !graphAbsorbs(s, global))
    }
  }
  for (const obj of generateJsonLd(globalSchemas, global)) {
    head.push(ldScript(obj as Record<string, unknown>))
  }

  // ── Shared schema-completeness builders (./schemaGraph.ts) ────────────────
  // Run BEFORE the site's pageTypeRules so vertical-specific extras (Place /
  // CollectionPage) layer on top of the shared Breadcrumb/Blog/FAQ base.
  //
  // BreadcrumbList from route depth (home ⇒ none).
  if (emitBreadcrumbs) {
    const titles = breadcrumbTitles?.(ctx) ?? {}
    const trail = breadcrumbTrailFromRoute(route, siteUrl, titles)
    for (const obj of buildBreadcrumbList(trail)) head.push(ldScript(obj))
  }

  // BlogPosting from frontmatter — only when the site marks this a blog route
  // and no pageTypeRule will also emit one (it would be a dup). We detect a
  // rule-emitted BlogPosting by checking the head we are about to add below, so
  // here we simply defer to the site: if it wired a BlogPosting rule, it should
  // leave `emitBlogPosting` off. (See README/option docs.)
  if (emitBlogPosting && (blogMatch ? blogMatch(ctx) : false)) {
    const blogMeta: BlogMeta = {
      headline: typeof fm.title === 'string' ? fm.title : slugToTitle(slug),
      datePublished: typeof fm.date === 'string' ? fm.date : undefined,
      dateModified: typeof fm.lastUpdated === 'string' ? fm.lastUpdated : undefined,
      url: canonical,
      image: typeof fm.headerImage === 'string' ? fm.headerImage : (typeof fm.image === 'string' ? fm.image : undefined),
      description: typeof fm.description === 'string' ? fm.description : undefined,
    }
    for (const obj of buildBlogPosting(blogMeta, siteUrl, global)) head.push(ldScript(obj))
  }

  // FAQPage from frontmatter.faq (honesty-gated; component-only FAQ ⇒ nothing).
  if (emitFaq) {
    const faq = Array.isArray(fm.faq) ? (fm.faq as FaqSource[]) : undefined
    for (const obj of buildFaqPage(faq)) head.push(ldScript(obj))
  }

  // Page-type JSON-LD: every matching site rule contributes its objects.
  for (const rule of pageTypeRules) {
    let matched = false
    try {
      matched = rule.match(ctx)
    } catch (err) {
      console.warn('[dcs-seo] pageTypeRule.match threw; skipping rule:', err)
      continue
    }
    if (!matched) continue
    let objects: Array<Record<string, unknown>> = []
    try {
      objects = rule.build(ctx) ?? []
    } catch (err) {
      console.warn('[dcs-seo] pageTypeRule.build threw; skipping rule output:', err)
      continue
    }
    for (const obj of objects) {
      if (obj) head.push(ldScript(obj))
    }
  }

  return {
    // Hand back the RAW title for `pageData.title`; VitePress applies its own
    // `titleTemplate` to produce the templated <title> element.
    title: rawTitle,
    head,
    description: description || undefined,
    setPageTitle: overrides.setPageTitle ?? false,
  }
}

/**
 * Compute the page-specific (un-templated) `seo.yaml` title for a slug, if any.
 * This is the title VitePress should template — it must NOT come pre-templated.
 */
function pageSpecificSeoTitle(
  slug: string,
  seoConfig: SeoConfiguration | undefined
): string | undefined {
  return seoConfig?.pages?.[slug]?.title || undefined
}

/**
 * Apply the global `titleTemplate` to a raw page title, matching the shared
 * resolver's precedence: a page that opts out (`noTitleTemplate`) or a
 * brand-complete global default is used verbatim; otherwise `%s` is filled.
 *
 * When `rawTitle` is the resolver's already-final title (e.g. the global
 * `defaultTitle`, used when there is no page-specific title), we return it
 * unchanged to avoid double-templating.
 */
function applyTitleTemplate(
  rawTitle: string,
  global: GlobalSeoConfig,
  resolved: ResolvedPageSeoLike
): string {
  // If rawTitle is exactly the resolver's final title, it is either already
  // templated (page-specific) or a brand-complete default — use as-is.
  if (rawTitle === resolved.title) return rawTitle
  if (!global.titleTemplate) return rawTitle
  return global.titleTemplate.replace('%s', rawTitle)
}

/** Structural subset of `ResolvedPageSeo` used by the title helper. */
interface ResolvedPageSeoLike {
  title: string
}

/**
 * Create a VitePress `transformPageData(pageData)` function that bakes DCS SEO
 * (global meta/OG/Twitter/canonical + global JSON-LD graph + pluggable
 * page-type JSON-LD) into the SSG `<head>`.
 *
 * Mutations performed on `pageData`:
 *   - **`frontmatter.head`** — the resolved tags are *appended* to any existing
 *     `head` (so site-level `head` config is preserved).
 *   - **`description`** — set to the resolved/overridden description so
 *     VitePress emits exactly one `description` meta (no duplicate; we do not
 *     push our own description meta).
 *   - **`title`** — set only when the site's `resolvePage` hook returns
 *     `setPageTitle: true` for this page, mirroring the bespoke behaviour where
 *     seo.yaml/per-type titles are authoritative but a post's frontmatter title
 *     is left intact.
 *
 * Defensive: never throws (a failure logs a warning and leaves `pageData`
 * untouched), so SEO can never break a production VitePress build.
 */
export function createSeoTransformPageData(
  options: CreateSeoTransformPageDataOptions
): (pageData: VitePressPageData) => void {
  return function transformPageData(pageData: VitePressPageData): void {
    try {
      const { head, title, description, setPageTitle } = buildVitePressSeoHead(pageData, options)

      pageData.frontmatter = pageData.frontmatter ?? {}
      const existing = (pageData.frontmatter.head as VitePressHeadConfig[] | undefined) ?? []
      pageData.frontmatter.head = [...existing, ...head]

      // Drive VitePress's own description meta from our resolved value so it is
      // not duplicated by the site-level default. (VitePress reads
      // pageData.description BEFORE transformPageData and renders the meta from
      // it, so overwriting the field here is the correct sink.)
      if (description) {
        pageData.description = description
      }

      // Make seo.yaml / per-type titles authoritative for <title> when the site
      // asks for it; VitePress then appends its titleTemplate.
      if (setPageTitle && title) {
        pageData.title = title
      }

      if (options.debug) {
        console.log(
          `[dcs-seo] transformPageData ${pageData.relativePath} → +${head.length} head tag(s)` +
            (setPageTitle && title ? ` (title="${title}")` : '')
        )
      }
    } catch (err) {
      console.warn(
        `[dcs-seo] createSeoTransformPageData failed for ${pageData?.relativePath}; head left unchanged:`,
        err
      )
    }
  }
}
